import type { BaseModel } from './baseModel';
import type { Company } from './company';
import type { OpenAIFunction } from './message';

export type FileJSON = {
  [originalFileName: string]: {
    bucketFilePath: string; // Path of the file in the bucket
    publicFileURL: string; // Public URL of the file (to be used in the frontend)
  };
};

export interface InsuranceBrokerCredential {
  company: string;
  username: string | null;
  password: string | null; // Can be '****' when returned from backend
  discount?: number | null; // 0-100 percentage
}

export type InsuranceBrokerCredentials = InsuranceBrokerCredential[];

export interface InsuranceCredentialsResponse {
  businessUnitId: number;
  insuranceBrokerCredentials: InsuranceBrokerCredentials;
}

export interface BusinessUnitDraft {
  name: string;
  phoneNumber: string;
  prompt: string;
  devUrl?: string;
  availableDates?: JSON;
  scheduleVisitInstructions?: string;
  AIFunctions?: OpenAIFunction[];
  carTypes?: string[] | null;
  carImages?: string[] | null;
  aiModel: string | null;
  images?: FileJSON | null;
  videos?: FileJSON | null;
  quotes?: FileJSON | null;
  documents?: FileJSON | null;
  mockPrompt?: string;
  availableFunctions?: string[] | null;
  pusherIdentification?: string | null;
  emailProperties?: {
    address?: string | null;
    provider?: 'gmail' | 'microsoft' | 'smtp' | null;
    draftMode?: boolean | null;
    smtpHost?: string | null;
    smtpPort?: number | null;
    imapHost?: string | null;
    imapPort?: number | null;
  } | null;
  phoneNumberTier?: string[] | null;
  type: 'car_dealership' | 'financial_institution' | 'insurance_broker' | 'after_sales' | null;
  messagesToNotify?: MessageToNotify[] | null;
  lookerReportUrl?: string | null;
  insuranceBrokerCredentials?: InsuranceBrokerCredentials | null;
  customReportInstructions?: string | null;
  reportSources?: string[] | null;
}

export interface MessageToNotify {
  description: string;
  examples: string[];
  slackChannelId?: string;
  emailsToNotify?: string[] | null;
  emailsToNotifyByBranchOffice?: Record<string, string[]> | null;
  fallbackEmailsToNotify?: string[] | null;
  isLastCRMUpdatedThreeDaysAgo?: boolean;
}

export type KanbanStatus =
  | 'CREATED'
  | 'HANDLED_BY_AI'
  | 'UPLOADED_TO_CRM'
  | 'PENDING_TO_BE_HANDLED'
  | 'PENDING_TO_BE_ASSIGNED'
  | 'ASSIGNED_TO_SALESMAN'
  | 'HANDLED_BY_SALESMAN'
  | 'CLOSED';

export interface BusinessUnitUpdate {
  hasSellerManagement?: boolean;
  hasClientQueueTableActive?: boolean;
  hasLeadClosureHidden?: boolean;
  hasCallingEnabled?: boolean;
  uploadSimulationsToCRM?: boolean;
  aiModel?: string | null;
  daysToCloseLeads?: number | null;
  customReportInstructions?: string | null;
  reportSources?: string[] | null;
  conversationTaggingConfig?: BusinessUnitConversationTaggingConfig | null;
  statusConfig?: KanbanStatus[] | null;
  websiteWhatsAppMessages?: string[] | null;
  websiteFormMessages?: string[] | null;
  mailingMessages?: string[] | null;
  tiktokMessages?: string[] | null;
}

export interface BusinessUnit extends BaseModel, BusinessUnitDraft {
  company: Company;
  hasSellerManagement: boolean;
  hasClientQueueTableActive: boolean;
  hasCallingEnabled: boolean;
  uploadSimulationsToCRM: boolean;
  customLeadClosedStates?: string[] | null;
  hasLeadClosureHidden: boolean;
  insuranceBrokerCredentials: InsuranceBrokerCredentials | null;
  daysToCloseLeads?: number | null;
  brokerBusinessUnitId?: number | null;
  conversationTaggingConfig?: BusinessUnitConversationTaggingConfig | null;
  statusConfig?: KanbanStatus[] | null;
  websiteWhatsAppMessages?: string[] | null;
  websiteFormMessages?: string[] | null;
  mailingMessages?: string[] | null;
  tiktokMessages?: string[] | null;
  /** List price of `aiModel` in USD per 1M tokens (input/output); only populated by the admin list endpoint. */
  aiModelCost?: { input: number; output: number } | null;
}

export interface BusinessUnitCostMetrics {
  totalFloidRequestsCost: number;
  totalAICost: number;
  totalWhatsAppCost: number;
  totalClients: number;
  totalLeads: number;
  costPerDay: Record<string, number>;
  costPerLeadSplitPerDay: Record<string, { ai: number; whatsApp: number; floid: number }>;
  averageMessageCostSplitPerDay: Record<string, { ai: number; whatsApp: number; floid: number }>;
  averageCostSplitPerDay: Record<string, { ai: number; whatsApp: number; floid: number }>;
  costRangesCount: {
    range: [number, number];
    count: number;
  }[];
}

export const INSURANCE_COMPANIES = ['SURA', 'HDI', 'BCI', 'ZURICH', 'REALE'] as const;
export type InsuranceCompany = (typeof INSURANCE_COMPANIES)[number];

export const INSURANCE_COMPANIES_MAP = {
  HDI: 'HDI Seguros',
  BCI: 'BCI Seguros',
  SURA: 'Seguros Sura',
  ZURICH: 'Zurich',
  REALE: 'Reale Seguros',
} satisfies Record<InsuranceCompany, string>;

export const TOPIC_COLORS = [
  '#EF4444',
  '#F97316',
  '#EAB308',
  '#22C55E',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#6B7280',
  '#0EA5E9',
  '#14B8A6',
  '#F59E0B',
  '#10B981',
] as const;

export type TopicColor = (typeof TOPIC_COLORS)[number];

export type TopicAssignmentMode = 'sticky' | 'manual';

export interface TopicConfig {
  name: string;
  description?: string | null;
  color: TopicColor;
  isActive: boolean;
  assignmentMode: TopicAssignmentMode;
}

export interface EndReasonConfig {
  name: string;
  description?: string | null;
}

export interface BusinessUnitConversationTaggingConfig {
  topics?: TopicConfig[] | null;
  endReasons?: EndReasonConfig[] | null;
  allowFreeFormTopics?: boolean;
  allowFreeFormEndReasons?: boolean;
}
